/** @internal – import from `@/store/useDepartmentStore` instead. */
import type { BrandInput } from "../types";
import { runSSEStream } from "../sseRunner";
import { buildErrorMessage } from "../utils";
import { sseEvents } from "../../../events";

/**
 * Research SSE pipeline runner. Posts to `/core/coagent/research` and
 * publishes typed `research:*` events on the department event bus
 * (`@/features/department/events`) as the server stream advances. The
 * department store subscribes to those events via
 * `wireResearchSubscriber` to apply the actual mutations — the runner
 * stays unaware of the store API.
 *
 * Migrated off the `SSECallbacks` mutator-bag in Phase 8 commit 71.
 */
export async function runResearchSSE(
  brandInput: BrandInput,
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  await runSSEStream(brandInput, {
    pipeline: "research",
    endpoint: "/core/coagent/research",
    sessionId,
    buildBody: (sid, input) => ({ sessionId: sid, brandInput: input }),
    errorPrefix: "Research",
    signal,
    debugMeta: { brandName: brandInput.brandName, hasAbortSignal: Boolean(signal) },
    handleEvent: (event) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { phase, status, data, error, details } = event as Record<string, any>;

      if (phase === "session_named" && data?.name) {
        sseEvents.publish({
          type: "research:session-named",
          pipeline: "research",
          sessionId,
          name: data.name,
        });
        return;
      }

      if (phase === "prompt_generation" && status === "complete") {
        sseEvents.publish({
          type: "research:queries-generated",
          pipeline: "research",
          sessionId,
          queryPlan: data.query_plan,
          count: data.query_count,
        });
        return;
      }

      if (phase === "data_collection" && status === "running") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const progress = (event as any).progress;
        sseEvents.publish({
          type: "research:data-collection-progress",
          pipeline: "research",
          sessionId,
          track: progress?.track || "data_collection",
          completed: progress?.completed || 0,
          total: progress?.total || 0,
        });
        return;
      }

      if (phase === "data_collection" && status === "complete") {
        sseEvents.publish({
          type: "research:data-collection-complete",
          pipeline: "research",
          sessionId,
          data,
        });
        return;
      }

      if (phase === "distillation" && status === "complete") {
        sseEvents.publish({
          type: "research:distillation-complete",
          pipeline: "research",
          sessionId,
          findingsCount: data.findings_count,
          sections: data.sections,
        });
        return;
      }

      if (phase === "synthesis" && status === "complete") {
        sseEvents.publish({
          type: "research:synthesis-complete",
          pipeline: "research",
          sessionId,
        });
        return;
      }

      if (phase === "done") {
        sseEvents.publish({
          type: "research:done",
          pipeline: "research",
          sessionId,
          data,
        });
        return;
      }

      if (phase === "error") {
        const message = buildErrorMessage(error, details, "Research pipeline failed");
        console.error("[coagent/research] SSE error", { sessionId, message, details });
        sseEvents.publish({
          type: "research:error",
          pipeline: "research",
          sessionId,
          message,
          ...(details !== undefined ? { details } : {}),
        });
      }
    },
  });
}
